import { YStack, XStack, Text, View, ScrollView } from "tamagui";
import { RiCustomerService2Line } from "react-icons/ri";
import RadiusCard from "@components/layout/RadiusCard";
import RadiusBoxLabel from "@components/layout/RadiusBoxLabel";
import { colors } from "@styles/colors";
import { PrimaryBtn } from "../Buttons";
import type { AddressData, WaitlistReason } from "../types";

interface WaitlistStepProps {
  reason: WaitlistReason;
  address: AddressData | null;
  onDone: () => void;
}

const REASON_TEXT: Record<WaitlistReason, { title: string; desc: string }> = {
  no_supplier_in_area: {
    title: "您的區域暫無合作瓦斯行",
    desc: "我們會盡快為您聯絡附近的瓦斯行加入平台",
  },
  user_rejected_recommendations: {
    title: "已為您安排專人協助",
    desc: "客服將依您的需求協助找到合適的瓦斯行",
  },
};

const STEPS = [
  { t: "為您尋找附近的瓦斯行", d: "我們會近期前往您所在區域,邀請附近的瓦斯行加入平台" },
  { t: "開通服務後主動聯絡", d: "一旦有合作瓦斯行可服務您的地址,客服會主動與您聯絡" },
  { t: "協助完成綁定", d: "確認後即可開始在平台上訂購瓦斯,輕鬆叫瓦斯" },
];

/** 候補頁：該區暫無合作瓦斯行 / 使用者拒絕所有推薦 → 交由客服後續聯絡。 */
const WaitlistStep = ({ reason, address, onDone }: WaitlistStepProps) => {
  const info = REASON_TEXT[reason] || REASON_TEXT.no_supplier_in_area;

  return (
    <YStack height="100dvh" width="100vw" background={colors.bg}>
      {/* 橘色大頭區 */}
      <YStack
        style={{ background: "linear-gradient(135deg, #F97316, #EA580C)" }}
        pt={56}
        pb={40}
        px="$5"
        ai="center"
        borderBottomLeftRadius={28}
        borderBottomRightRadius={28}>
        <View width={88} height={88} borderRadius={44} background="rgba(255,255,255,0.18)" ai="center" jc="center" mb="$4">
          <RiCustomerService2Line size={40} color={colors.light} />
        </View>
        <Text fontSize={22} fontWeight="800" color={colors.light} mb="$1.5" ta="center">
          {info.title}
        </Text>
        <Text fontSize={13} color="rgba(255,255,255,0.9)" ta="center" lineHeight={20}>
          {info.desc}
        </Text>
      </YStack>

      <ScrollView flex={1} showsVerticalScrollIndicator={false} contentContainerStyle={{ padding: 16 }}>
        <YStack gap={12}>
          {/* 接下來會發生什麼 */}
          <RadiusCard>
            <RadiusBoxLabel label="接下來會發生什麼?" icon={<Text fontSize={14}>🚀</Text>} theme="amber" />
            <YStack mt="$2">
              {STEPS.map((s, i) => (
                <XStack key={i} gap="$3" ai="stretch">
                  <YStack ai="center" width={26}>
                    <View
                      width={26}
                      height={26}
                      borderRadius={13}
                      ai="center"
                      jc="center"
                      style={{ background: "linear-gradient(135deg, #F97316, #EA580C)" }}>
                      <Text fontSize={13} fontWeight="800" color={colors.light}>
                        {i + 1}
                      </Text>
                    </View>
                    {i < STEPS.length - 1 && <View flex={1} width={2} background="#FED7AA" mt="$1" minHeight={18} />}
                  </YStack>
                  <YStack flex={1} pb={i < STEPS.length - 1 ? 18 : 0}>
                    <Text fontSize={14} fontWeight="700" color={colors.grayDark} mb="$0.5">
                      {s.t}
                    </Text>
                    <Text fontSize={12} color={colors.sub} lineHeight={18}>
                      {s.d}
                    </Text>
                  </YStack>
                </XStack>
              ))}
            </YStack>
          </RadiusCard>

          {/* 已記錄您的需求 */}
          <RadiusCard>
            <RadiusBoxLabel label="已記錄您的需求" icon={<Text fontSize={14}>📋</Text>} />
            <YStack mt="$2">
              <XStack gap="$2.5" py="$2.5" borderBottomWidth={1} borderBottomColor={colors.borderLight} borderStyle="dashed" ai="flex-start">
                <Text fontSize={16}>📍</Text>
                <YStack flex={1}>
                  <Text fontSize={11} color={colors.hint} fontWeight="600">
                    配送地址
                  </Text>
                  <Text fontSize={13} color={colors.grayDark} lineHeight={18}>
                    {address?.address}
                  </Text>
                </YStack>
              </XStack>
              <XStack gap="$2.5" py="$2.5" ai="flex-start">
                <Text fontSize={16}>✅</Text>
                <YStack flex={1}>
                  <Text fontSize={11} color={colors.hint} fontWeight="600">
                    已驗證手機
                  </Text>
                  <Text fontSize={13} color={colors.grayDark}>
                    客服將致電聯繫您
                  </Text>
                </YStack>
              </XStack>
            </YStack>
          </RadiusCard>

          <RadiusCard bg="rgba(255, 255, 255, 0.3)" border="#e2e8f0" borderRadius={10}>
            <Text fontSize={12} color={colors.gray} lineHeight={18}>
              💡 等候期間您仍可瀏覽平台,客服處理完畢會立即通知您
            </Text>
          </RadiusCard>
        </YStack>
      </ScrollView>

      <YStack background={colors.light} borderTopWidth={1} borderTopColor={colors.borderLight} p="$3" style={{ paddingBottom: "max(12px, env(safe-area-inset-bottom))" }}>
        <PrimaryBtn onPress={onDone}>了解,先瀏覽平台</PrimaryBtn>
      </YStack>
    </YStack>
  );
};

export default WaitlistStep;
